import Catalogue from "./catalogue"
import _ from "lodash"

const getDefaultParticipantCount = (activityId, regionCode) => {
  // the participants.min property is not reliable enough ... hence we find the lowest in the priceConfigurations
  let activity = Catalogue.getActivityById(activityId)
  let regionVariant = activity.regionVariants.find(rv => rv.regionCode === regionCode)
  let priceConfigurations = regionVariant.priceConfigurations
  let participantCount = priceConfigurations.reduce((acc, curr) => {
    return curr.participantCount < acc ? curr.participantCount : acc
  }, 999999)

  return participantCount
}

const getDefaultRegionCode = (activity) => {
  return getDefaultVariant(activity).regionCode 
}

/**
 * Returns the default region variant of an activity
 * @param {*} activity 
 */
const getDefaultVariant = (activity) => {
  return activity.regionVariants.filter(rv => rv.isActive)[0]
}

/**
 * Returns a region variant of an activity
 * @param {*} activity 
 * @param {*} regionCode 
 */
const getVariant = (activity, regionCode) => {
  return activity.regionVariants.find(rv => rv.regionCode === regionCode)
}

/**
 * Returns all available options of a given variant of an activity
 * @param {*} activity 
 * @param {*} regionCode 
 */
const getOptions = (activity, regionCode) => {
  const regionVariant = getVariant(activity, regionCode)
  return regionVariant.options
}

/**
 * Returns all options, no matter the region - includes optional subactivities
 * @param {*} activity 
 */
const getAllOptions = (activity) => {
  // options (from all region variants)
  const allOptions = activity.regionVariants
    .reduce((prev, curr) => {
      return _.concat(prev, curr.options)
    }, [])
    .filter(o => o.name.de)
    .map(o => {
      return {name:o.name.de} // price and number of units varies from region to region, if we supply those values we need to evaluate min price, min minUnits, min maxUnits
    });
  
  // optional subactivities
  const allOptionalSubactivities = activity.subactivities
    .filter(sa => sa.isOptional)
    .map(sa => {
      return {name:sa.title};
    });

  // options & optional activities
  const all = allOptions.concat(allOptionalSubactivities);
  
  // make sure no duplicate items appear
  const uniqueOptions = _.uniqBy(all, item => item.name);
  return uniqueOptions;
}

/**
 * Returns all price configurations for a given variant of an activity
 * @param {*} activity 
 * @param {*} regionCode 
 */
const getPriceConfigurations = (activity, regionCode) => {
  const regionVariant = getVariant(activity, regionCode)
  return regionVariant.priceConfigurations
}

/**
 * Returns the `availableAreas` string property of a region variant
 * @param {*} activity 
 * @param {*} regionCode 
 */
const getAvailableAreas = (activity, regionCode) => {
  const regionVariant = getVariant(activity, regionCode)
  if(regionVariant && (regionVariant.availableAreas || "").trim().length > 0)  {
    return regionVariant.availableAreas.trim()
  }
  else {
    return null
  }
}


/**
 * Returns an activity's subactivities for a given region and number of participants
 * @param {*} activity 
 * @param {*} regionCode 
 * @param {*} participantCount 
 * @param {*} includeOptionals
 */
const getSubactivities = (activity, regionCode, participantCount, includeOptionals) => {
  const subactivities = activity.subactivities
  .filter(sa => includeOptionals ? true : sa.isOptional !== true)
  .map(sa => {
    // get the applicable region variant of the subactivity
    const sarv = sa.regionVariants.find(item => item.regionCode === regionCode)
    if(sarv) {
      // get the applicable price configuration of the subactivity
      const sarvpc = sarv.priceConfigurations.find(item => item.participantCount === participantCount)
      if(sarvpc) {
        return {
          activity_id:sa._id,
          isOptional:sa.isOptional,
          title:sa.title,
          description:sa.shortDescription,
          participantCount:sarvpc.participantCount,
          price:sarvpc.price
        }
      }
    }
    return null
  })
  .filter(sa => sa !== null)

  return subactivities
}

/**
 * Returns an activity's optional subactivities for a given region and number of participants
 * @param {*} activity 
 * @param {*} regionCode 
 * @param {*} participantCount 
 */
const getOptionalSubactivities = (activity, regionCode, participantCount) => {
  const subactivities = getSubactivities(activity, regionCode, participantCount, true)
  const optionals = subactivities.filter(sa => sa.isOptional)

  return optionals
}

/**
 * Creates an activity object formated to fit into an adventure
 * @param {*} activity 
 * @param {*} regionCode 
 * @param {*} options 
 * @param {*} participantCount 
 * @param {*} subactivities
 */
const createAdventureActivity = (activity, regionCode, options, participantCount, subactivities) => {
  // basic adventure activity object
  let adventureActivity = {
    //Activity: regionVariant,
    Activity_ID: activity._id,
    ActivityInfo: {
      images:activity.images,
      title:activity.title,
      slug:activity.slug
    }, // TODO necessary? we add this to make getting images, titles easier
    //CalculatedPrice: price,
    ParticipantCount: participantCount,
    RegionCode: regionCode,
    IsHomeDelivery: activity.isHomeDelivery || false,
    IsOnlineEvent: activity.isOnlineEvent || false,
    AvailableTimes: activity.availableTimes || "",
    Options: [],
    Subactivities: [],
    _parent_id:activity._id // TODO #fud check why we need this ...
  }

  // get and set region variant
  const regionVariant = getVariant(activity, regionCode)
  adventureActivity.Activity = regionVariant
  adventureActivity.ID = regionVariant._id // TODO #fud do we (or the API) ever need this?
  adventureActivity.AvailableAreas = regionVariant.availableAreas || ""

  // get the base price
  const priceConfiguration = getPriceConfigurations(activity, regionCode).find(pc => pc.participantCount === participantCount)
  adventureActivity.CalculatedPrice = priceConfiguration.price
  
  // add options and add their price to activity's price
  const opts = options || []
  opts
    .filter(o => o.count > 0)
    .forEach(o => {
      // get the option from the region variant
      let option = regionVariant.options.find(opt => opt._id === o.id)

      // update activity price
      adventureActivity.CalculatedPrice += option.price * o.count

      // add the option to adventure activity
      adventureActivity.Options.push({
        ID:option._id,
        UnitCount:o.count,
        ActivityOption: {
          _id:option._id,
          price:option.price,
          maxUnits:option.maxUnits,
          minUnits:option.minUnits,
          description:option.description,
          name:option.name
        },
        PriceSelection:[] // TODO missing ... don't think this is ever used
      })
    })

  // add subactivities, add price if they are options
  const sas = subactivities || []
  sas.forEach(sa => {
    adventureActivity.Subactivities.push(_.clone(sa))
    // treated as an option? add to price
    if(sa.isOptional) {
      adventureActivity.CalculatedPrice += sa.price
    }
  })

  return adventureActivity
}

const getDefaultImage = (activity) => {
  let image = {url_thumbnail:""}; // TODO placeholder image
  try {
    if(activity.images && activity.images.length > 0) {
      image = activity.images.find(i => i.default) || activity.images[0];
    }
  }
  catch {
    console.error(`could not getDefaultImage() for activity ${activity._id}, ${activity.title}`);
  }
  return image;
}


/**
 * Looks at all region variants and their price configurations to find the lowest price per Person
 * @param {Object} activity 
 */
const getMinPricePerPerson = (activity) => {
  try {
    let minPrice = 9999999;
    activity.regionVariants.forEach(rv => {
      // get smallest configuration
      const smallest = rv.priceConfigurations.sort((a,b) => a.participantCount > b.participantCount ? 1 : 1)[0];
      // calculate pp
      const pp = smallest.price / smallest.participantCount;
      if(pp < minPrice) {
        minPrice = pp;
      }
    })
    return minPrice;
  }
  catch(err) {
    console.error("error evaluating min price per person:", _.get(activity, "title"), err);
    return activity.regionVariants[0].priceConfigurations[0].price;
  }
}

const getPrice = (activity, regionCode, participantCount, options, subactivities) => {
  let calculatedPrice = 0

  // get region variant
  const regionVariant = getVariant(activity, regionCode)
  //adventureActivity.Activity = regionVariant
  //adventureActivity.ID = regionVariant._id // TODO #fud do we (or the API) ever need this?

  // get the base price
  const priceConfiguration = getPriceConfigurations(activity, regionCode).find(pc => pc.participantCount === participantCount)
  calculatedPrice = priceConfiguration.price

  // add options and add their price to activity's price
  options
    .filter(o => o.count > 0)
    .forEach(o => {
      // get the option from the region variant
      let option = regionVariant.options.find(opt => opt._id === o.id)

      // update activity price
      calculatedPrice += option.price * o.count
    })

  // add the optional subactivities (which are like options)
  subactivities
    .filter(sa => sa.isOptional)
    .forEach(sa => {
      // update activity price
      calculatedPrice += sa.price
    })


  // done and return
  return calculatedPrice
}

export default {
  createAdventureActivity,

  getDefaultParticipantCount,
  getDefaultRegionCode,
  getDefaultVariant,
  getDefaultImage,
  getMinPricePerPerson,
  getPrice,

  getVariant,
  getOptions,
  getAllOptions,
  getPriceConfigurations,
  getAvailableAreas,
  getSubactivities,
  getOptionalSubactivities
}